// Context Panel for Inbox v2 (PRD §1.7, WP-04).
// Customer profile, Bot/AI toggles, Labels manager, Team member assignment, Custom fields, Notes list, Open in Subscribers.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../lib/i18n';
import { useTeam } from '../../lib/team';
import {
  updateConversation,
  addConversationNote,
  useConversationNotes,
  type LiveConversation,
} from '../../lib/inbox';
import { useLabels } from '../../lib/crm';
import { resetInputFlow } from '../../lib/inputFlows';
import Toggle from '../ui/Toggle';
import { useToast } from '../ui/Toast';
import { CloseIcon, UserIcon, ExternalLinkIcon, PlusIcon, RefreshIcon } from '../icons';

interface ContextPanelProps {
  conv: LiveConversation;
  botOn: boolean;
  aiOn: boolean;
  onBot: (v: boolean) => void;
  onAi: (v: boolean) => void;
  onClose?: () => void;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-line px-4 py-3 dark:border-slate-800">
      <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-ink-4 dark:text-slate-400">
        {title}
      </h4>
      {children}
    </div>
  );
}

function fmtNoteTime(ms: number): string {
  if (!ms) return '';
  return new Date(ms).toLocaleString([], {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function ContextPanel({
  conv,
  botOn,
  aiOn,
  onBot,
  onAi,
  onClose,
}: ContextPanelProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { members } = useTeam();
  const { labels: availableLabels } = useLabels();
  const notes = useConversationNotes(conv.id);

  const [addingNote, setAddingNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [showAddLabel, setShowAddLabel] = useState(false);
  const [resettingFlow, setResettingFlow] = useState(false);
  const toast = useToast();

  const handleResetFlow = async () => {
    setResettingFlow(true);
    try {
      await resetInputFlow({ conversationId: conv.id });
      toast.success(t('inbox.ctx.flowResetSuccess'));
    } catch (e: any) {
      toast.error(e.message || t('inbox.ctx.flowResetFailed'));
    } finally {
      setResettingFlow(false);
    }
  };

  const handleSaveNote = async () => {
    const body = noteText.trim();
    if (!body) return;
    setNoteText('');
    setAddingNote(false);
    try {
      await addConversationNote({ conversationId: conv.id, text: body });
    } catch (err) {
      toast.error((err as Error).message || t('common.saveFailed'));
    }
  };

  const handleAssign = async (uid: string | null) => {
    try {
      await updateConversation({
        conversationId: conv.id,
        assignedTo: uid,
        assignee: uid,
      });
    } catch (err) {
      toast.error((err as Error).message || t('common.saveFailed'));
    }
  };

  const handleAddLabel = async (labelName: string) => {
    if (conv.labels.includes(labelName)) return;
    const nextLabels = [...conv.labels, labelName];
    setShowAddLabel(false);
    try {
      await updateConversation({
        conversationId: conv.id,
        labels: nextLabels,
      });
    } catch (err) {
      toast.error((err as Error).message || t('common.saveFailed'));
    }
  };

  const handleRemoveLabel = async (labelName: string) => {
    const nextLabels = conv.labels.filter((l) => l !== labelName);
    try {
      await updateConversation({
        conversationId: conv.id,
        labels: nextLabels,
      });
    } catch (err) {
      toast.error((err as Error).message || t('common.saveFailed'));
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-white dark:bg-slate-900 overflow-y-auto transition-colors">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-line px-4 py-3 dark:border-slate-800">
        <div>
          <b className="block text-xs font-bold text-ink dark:text-slate-100">
            {conv.displayName || conv.name}
          </b>
          <span className="text-[11px] text-ink-3 dark:text-slate-400">{conv.phone}</span>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <CloseIcon size={16} />
          </button>
        )}
      </div>

      {/* Customer Profile Section (§1.7) */}
      <Section title={t('inbox.ctx.profile') || 'Customer Profile'}>
        <div className="space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-ink-3 dark:text-slate-400">{t('inbox.ctx.customerSince') || 'Customer Since'}</span>
            <b className="text-ink dark:text-slate-200">{conv.subscribedAt || '—'}</b>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-3 dark:text-slate-400">{t('inbox.ctx.lastSeen') || 'Last Active'}</span>
            <b className="text-ink dark:text-slate-200">{conv.time || '—'}</b>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-3 dark:text-slate-400">{t('inbox.ctx.channel') || 'Channel'}</span>
            <b className="text-ink dark:text-slate-200 uppercase">{conv.channel}</b>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-3 dark:text-slate-400">{t('inbox.ctx.status') || 'Status'}</span>
            <span className="rounded bg-slate-100 px-1.5 py-0.2 text-[10px] font-bold uppercase text-slate-700 dark:bg-slate-800 dark:text-slate-300">
              {conv.status}
            </span>
          </div>
        </div>

        <div className="mt-3">
          <button
            type="button"
            onClick={() => navigate(`/subscribers?search=${encodeURIComponent(conv.phone)}`)}
            className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-line bg-surface py-2 text-xs font-bold text-slate-700 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-750"
          >
            <ExternalLinkIcon size={13} />
            <span>{t('inbox.ctx.openInSubscribers') || 'Open in Subscribers'}</span>
          </button>
        </div>
      </Section>

      {/* Actions (Bot / AI Reply toggles) */}
      <Section title={t('inbox.ctx.automation') || 'Automation'}>
        <div className="flex items-center justify-between py-1.5 text-xs font-semibold text-ink dark:text-slate-200">
          <span>{t('inbox.ctx.botReply') || 'Bot Auto-Reply'}</span>
          <Toggle on={botOn} onChange={onBot} label={t('inbox.ctx.botReply')} />
        </div>
        <div className="flex items-center justify-between py-1.5 text-xs font-semibold text-ink dark:text-slate-200">
          <span>{t('inbox.ctx.aiAgent') || 'AI Agent'}</span>
          <Toggle on={aiOn} onChange={onAi} label={t('inbox.ctx.aiAgent')} />
        </div>
        <div className="pt-2">
          <button
            type="button"
            onClick={handleResetFlow}
            disabled={resettingFlow}
            className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-line bg-surface py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-750 transition"
          >
            <RefreshIcon size={12} className={resettingFlow ? 'animate-spin' : ''} />
            <span>{t('inbox.ctx.resetFlow')}</span>
          </button>
        </div>
      </Section>

      {/* Team Member Assignment */}
      <Section title={t('inbox.ctx.assignedAgent') || 'Assigned Agent'}>
        <div className="flex items-center gap-2">
          <UserIcon size={15} className="text-slate-400 shrink-0" />
          <select
            value={conv.assignedTo || ''}
            onChange={(e) => void handleAssign(e.target.value || null)}
            className="w-full rounded-xl border border-line bg-surface px-2.5 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
          >
            <option value="">{t('inbox.ctx.unassigned') || 'Unassigned'}</option>
            {members.map((m) => (
              <option key={m.uid} value={m.uid}>
                {m.email} {m.isSelf ? '(You)' : ''}
              </option>
            ))}
          </select>
        </div>
      </Section>

      {/* Labels */}
      <Section title={t('inbox.ctx.labels') || 'Labels'}>
        <div className="flex flex-wrap gap-1.5">
          {conv.labels.map((l) => (
            <span
              key={l}
              className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-bold text-brand dark:bg-brand-950/40 dark:text-brand-tint"
            >
              <span>{l}</span>
              <button
                type="button"
                onClick={() => void handleRemoveLabel(l)}
                className="text-brand/60 hover:text-brand"
              >
                ✕
              </button>
            </span>
          ))}

          {showAddLabel ? (
            <div className="mt-1 w-full space-y-1">
              <select
                autoFocus
                onChange={(e) => {
                  if (e.target.value) void handleAddLabel(e.target.value);
                }}
                className="w-full rounded-lg border border-line bg-surface px-2 py-1 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
              >
                <option value="">— Select label —</option>
                {availableLabels
                  .filter((lbl) => !conv.labels.includes(lbl.name))
                  .map((lbl) => (
                    <option key={lbl.id} value={lbl.name}>
                      {lbl.name}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                onClick={() => setShowAddLabel(false)}
                className="text-[11px] text-slate-500 hover:underline"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowAddLabel(true)}
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-line px-2 py-0.5 text-xs text-slate-500 hover:border-brand hover:text-brand dark:border-slate-700"
            >
              <PlusIcon size={12} />
              <span>{t('inbox.ctx.addLabel') || 'Add label'}</span>
            </button>
          )}
        </div>
      </Section>

      {/* Custom Fields */}
      {conv.customFields && conv.customFields.length > 0 && (
        <Section title={t('inbox.ctx.customFields') || 'Custom Fields'}>
          <div className="space-y-1.5 text-xs">
            {conv.customFields.map((f) => (
              <div key={f.name} className="flex justify-between gap-2">
                <span className="text-ink-3 dark:text-slate-400 truncate">{f.name}</span>
                <b className="text-ink dark:text-slate-200 truncate">{f.value}</b>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Internal Notes */}
      <Section title={t('inbox.ctx.notes') || 'Internal Notes'}>
        {notes.length > 0 && (
          <ul className="mb-2 space-y-2">
            {notes.map((n) => (
              <li
                key={n.id}
                className="rounded-xl border border-amber-200 bg-amber-50/70 p-2.5 text-xs text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100"
              >
                <p className="whitespace-pre-wrap break-words leading-relaxed">{n.text}</p>
                <p className="mt-1 text-[10px] text-amber-800/70 dark:text-amber-400/70">
                  {n.byEmail ? `${n.byEmail} · ` : ''}
                  {fmtNoteTime(n.createdAt)}
                </p>
              </li>
            ))}
          </ul>
        )}

        {addingNote ? (
          <div className="space-y-2">
            <textarea
              autoFocus
              rows={2}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder={t('inbox.ctx.notePlaceholder') || 'Add a note…'}
              className="w-full rounded-xl border border-line bg-surface p-2.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSaveNote}
                disabled={!noteText.trim()}
                className="rounded-lg bg-brand px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-brand-dark disabled:opacity-50"
              >
                {t('common.save') || 'Save'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAddingNote(false);
                  setNoteText('');
                }}
                className="rounded-lg border border-line px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAddingNote(true)}
            className="flex items-center gap-1 text-xs font-bold text-brand hover:underline"
          >
            <PlusIcon size={13} />
            <span>{t('inbox.ctx.addNote') || 'Add Note'}</span>
          </button>
        )}
      </Section>
    </div>
  );
}
